import { FC, useState } from 'react';
import { SCUIButton, SCUINoRecordComponent, SCUILoadingLinear } from '../../atomic/atoms';
import { SCUIContent } from '../../atomic/organisms';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import { SCUIBreadCrumb, SCUITextFieldWithSelectBox } from '../../atomic/molecules';
import { Link } from 'react-router-dom';
import {
    Grid,
    FormControl,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Box,
    Tooltip,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { ProductSearchServiceQuery } from '../../services/query/';
import { SCUIProductBatchSearchModal } from './components';
import { noImageIcon } from '../../assets/images';
import { PS } from './productSearch.classes';

export const ProductSearch: FC<any> = () => {
    const { t } = useTranslation();
    const productEnv = JSON.parse(import.meta.env.VITE_PRODUCT_DICTIONARY!);
    const cultureCode = localStorage.getItem('i18nextLng') || 'tr-TR';

    const [searchedText, setSearchedText] = useState('');
    const [listModalOpen, setListModalOpen] = useState(false);
    const [datas, setDatas] = useState<any>();
    const [documentType, setDocumentType] = useState(1);
    const [loading, setLoading] = useState(false);
    const [isStock, setISStock] = useState(false);

    const handleCloseModal = () => {
        setListModalOpen(false);
    };

    const handleOpenModal = () => {
        setSearchedText('');
        setListModalOpen(true);
    };

    const selectListOptions = [
        { value: '1', text: t('searchService.optionId') },
        { value: '2', text: t('searchService.specialAndColorCode') },
        { value: '3', text: t('searchService.barcode') },
        { value: '4', text: t('searchService.ProductOptionSizeRef') },
        { value: '5', text: t('searchService.specialCode') },
    ];

    const getSearchTypeText = () => {
        return (
            selectListOptions.find((option) => option.value === String(documentType))?.text ||
            t('searchService.optionId')
        );
    };

    const buildObj = async () => {
        if (documentType == 1) {
            return {
                values: searchedText.trim().split('\n'),
                warehouseGroupId: productEnv[0].WarehouseGroupId,
                productSummaryRequestType: 0,
            };
        } else {
            let req = {
                searchType: Number(documentType),
                searchKeys: searchedText.trim().split('\n'),
            };
            const res = await ProductSearchServiceQuery.GetOptionIds(req);
            return {
                values: res.map((item: any) => item.optionId.toString()),
                warehouseGroupId: productEnv[0].WarehouseGroupId,
                productSummaryRequestType: 0,
            };
        }
    };

    const listele = async () => {
        setLoading(true);
        try {
            const obj = await buildObj();
            const res = await ProductSearchServiceQuery.GetMultipleOptionId(obj);

            const errorItems = res.data.items.filter((item: any) => item.option === null);
            if (errorItems.length > 0) {
                const errorIds = errorItems
                    .map((item: any) => `<li>${item.message?.trim().split(',')[0]}</li>`)
                    .join('');
                const errorList = `<ul>${errorIds}</ul>`;
                const searchTypeText = getSearchTypeText();
                Swal.fire({
                    icon: 'error',
                    title: t('general.error'),
                    html: `${t('productSearch.search.notFoundWithType', { searchType: searchTypeText })} ${errorList}`,
                });
                setLoading(false);
                return;
            } else if (res.data.message === 'OptionIds not found!') {
                const searchTypeText = getSearchTypeText();
                Swal.fire({
                    icon: 'error',
                    title: t('general.error'),
                    text: t('productSearch.search.noProductFoundForType', { searchType: searchTypeText }),
                });
                setLoading(false);
                return;
            }
            let parseData = res.data.items.map((item: any) => {
                return {
                    categories: item.option.categories[0]?.cultureValueList[cultureCode],
                    productName: item.option.modelTitle[cultureCode],
                    ozelKod1: item.option.ozelKod1,
                    renkKod: item.option.renkKod,
                    picture: item.option.medias === null ? noImageIcon : item.option.medias[0].mediaPath,
                    optionId: item.option.optionId,
                    gender: item.option.gender.cultureValueList[cultureCode],
                };
            });
            if (res.data) {
                setISStock(res.data.hasStock);
                setDatas(parseData);
            }
        } catch (error: any) {
            console.error('Error in listele:', error);
            const searchTypeText = getSearchTypeText();
            const errorMessage = error?.response?.data?.Message || error?.message;

            if (errorMessage) {
                Swal.fire({
                    icon: 'error',
                    title: t('general.error'),
                    html: `<strong>${t('productSearch.search.searchTypeLabel', {
                        searchType: searchTypeText,
                    })}</strong><br/><br/>${errorMessage}`,
                });
            } else {
                Swal.fire({
                    icon: 'error',
                    title: t('general.error'),
                    text: t('productSearch.search.noProductFoundForType', { searchType: searchTypeText }),
                });
            }
        } finally {
            setLoading(false);
        }
    };

    const getGenderChip = (gender: string) => {
        const lower = gender?.toLowerCase() ?? '';
        const chipSx = { fontWeight: 600, fontSize: '0.95rem', height: 32, '& .MuiChip-label': { px: 1.5 } };

        if (lower.includes('erkek') || lower.includes('male') || lower.includes('man')) {
            return (
                <Chip
                    label={gender}
                    sx={{ ...chipSx, backgroundColor: '#EFF6FF', color: '#1D4ED8', border: '1px solid #BFDBFE' }}
                />
            );
        }
        if (lower.includes('kadın') || lower.includes('kadin') || lower.includes('female') || lower.includes('woman')) {
            return (
                <Chip
                    label={gender}
                    sx={{ ...chipSx, backgroundColor: '#FDF2F8', color: '#BE185D', border: '1px solid #FBCFE8' }}
                />
            );
        }
        return (
            <Chip
                label={gender}
                sx={{ ...chipSx, backgroundColor: '#F1F5F9', color: '#64748B', border: '1px solid #CBD5E1' }}
            />
        );
    };

    const headerCellSx = {
        borderBottom: '2px solid #E5E7EB',
        backgroundColor: '#F9FAFB',
        textAlign: 'center' as const,
        fontWeight: 700,
        fontSize: '0.72rem',
        color: '#6B7280',
        textTransform: 'uppercase' as const,
        letterSpacing: '0.05em',
        py: 1.5,
        whiteSpace: 'nowrap' as const,
    };

    const bodyCellSx = {
        borderBottom: 'none',
        textAlign: 'center' as const,
        verticalAlign: 'middle' as const,
        fontSize: '0.875rem',
        color: '#111827',
        py: 0,
    };

    return (
        <SCUIContent className={PS.page}>
            <Grid
                size={{
                    sm: 12,
                    md: 12,
                    lg: 12,
                    xl: 12,
                }}
            >
                <Grid className={PS.breadcrumbSection} container direction="row" spacing={2} sx={{ marginLeft: '0.5rem' }}>
                    <SCUIBreadCrumb
                        title={t('searchService.searchProduct')}
                        componentLink={'/ProductSearch'}
                        style={{ cursor: 'pointer' }}
                        onClick={() => {
                            setDatas('');
                            setSearchedText('');
                        }}
                    />
                    <div className={PS.loadingSection}>
                        <SCUILoadingLinear display={loading} />
                    </div>
                </Grid>
                <Grid
                    size={{
                        sm: 12,
                        md: 12,
                        lg: 12,
                        xl: 12,
                    }}
                >
                    <Grid
                        size={{
                            sm: 12,
                            md: 12,
                            lg: 12,
                            xl: 12,
                        }}
                    >
                        {datas ? (
                            <div className={PS.resultsSection}>
                                {datas.map((item: any, index: any) => {
                                return (
                                    <Paper
                                        key={index}
                                            className={PS.resultCard}
                                        sx={{
                                            marginTop: '16px',
                                            borderRadius: '12px',
                                            border: '1px solid #E5E7EB',
                                            boxShadow: '0 1px 3px 0 rgba(0,0,0,0.08), 0 4px 16px 0 rgba(0,0,0,0.06)',
                                            overflow: 'hidden',
                                            transition: 'box-shadow 0.2s ease, transform 0.2s ease',
                                            '&:hover': {
                                                boxShadow:
                                                    '0 4px 12px 0 rgba(0,0,0,0.12), 0 8px 24px 0 rgba(0,0,0,0.08)',
                                                transform: 'translateY(-1px)',
                                            },
                                        }}
                                    >
                                            <Box className={PS.resultCardLayout} sx={{ display: 'flex', flexDirection: 'row', alignItems: 'stretch' }}>
                                            <Tooltip
                                                title={
                                                    <Box
                                                            className={PS.imageTooltipImg}
                                                        component="img"
                                                        src={item.picture}
                                                        alt={item.productName}
                                                        sx={{
                                                            display: 'block',
                                                            maxWidth: 340,
                                                            maxHeight: 440,
                                                            width: 'auto',
                                                            height: 'auto',
                                                            objectFit: 'contain',
                                                        }}
                                                    />
                                                }
                                                placement="right"
                                                arrow={false}
                                                slotProps={{
                                                    tooltip: {
                                                        sx: {
                                                            bgcolor: 'transparent',
                                                            p: 0,
                                                            maxWidth: 'none',
                                                            boxShadow: '0 8px 32px rgba(0,0,0,0.28)',
                                                            borderRadius: '10px',
                                                            overflow: 'hidden',
                                                        },
                                                    },
                                                }}
                                            >
                                                <Box
                                                        className={PS.imageThumbWrap}
                                                    sx={{
                                                        width: 110,
                                                        minWidth: 110,
                                                        overflow: 'hidden',
                                                        position: 'relative',
                                                        backgroundColor: '#F9FAFB',
                                                        borderRight: '1px solid #E5E7EB',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        cursor: 'zoom-in',
                                                        '& img': {
                                                            width: '100%',
                                                            height: '100%',
                                                            objectFit: 'cover',
                                                            transition: 'transform 0.25s ease, opacity 0.25s ease',
                                                            display: 'block',
                                                        },
                                                        '&:hover img': {
                                                            transform: 'scale(1.08)',
                                                            opacity: 0.85,
                                                        },
                                                    }}
                                                >
                                                        <img className={PS.imageThumb} src={item.picture} alt={item.productName} />
                                                </Box>
                                            </Tooltip>
                                                <TableContainer className={PS.tableWrap} sx={{ flex: 1 }}>
                                                <Table
                                                        className={PS.table}
                                                    sx={{ minWidth: 600, height: '100%' }}
                                                    aria-label="product table"
                                                >
                                                        <TableHead className={PS.tableHead}>
                                                            <TableRow className={PS.tableHeadRow}>
                                                            <TableCell sx={headerCellSx}>
                                                                {t('searchService.specialAndColorCode')}
                                                            </TableCell>
                                                            <TableCell sx={headerCellSx}>
                                                                {t('general.productName')}
                                                            </TableCell>
                                                            <TableCell sx={headerCellSx}>
                                                                {t('searchService.gender')}
                                                            </TableCell>
                                                            <TableCell sx={headerCellSx}>
                                                                {t('general.category')}
                                                            </TableCell>
                                                            <TableCell sx={headerCellSx}>
                                                                {t('general.stock')}
                                                            </TableCell>
                                                            <TableCell
                                                                sx={{
                                                                    ...headerCellSx,
                                                                    borderBottom: '2px solid #E5E7EB',
                                                                }}
                                                            />
                                                        </TableRow>
                                                    </TableHead>
                                                        <TableBody className={PS.tableBody}>
                                                            <TableRow className={PS.tableBodyRow} sx={{ height: '100%', verticalAlign: 'middle' }}>
                                                                <TableCell className={PS.cellSpecialColor} sx={bodyCellSx}>
                                                                <Box
                                                                    sx={{
                                                                        display: 'inline-flex',
                                                                        alignItems: 'center',
                                                                        gap: 0.5,
                                                                        backgroundColor: '#F3F4F6',
                                                                        border: '1px solid #E5E7EB',
                                                                        borderRadius: '6px',
                                                                        px: 2,
                                                                        py: 0.75,
                                                                        fontFamily: 'monospace',
                                                                        fontSize: '1.5rem',
                                                                        fontWeight: 600,
                                                                        color: '#374151',
                                                                        letterSpacing: '0.02em',
                                                                    }}
                                                                >
                                                                    {item.ozelKod1}
                                                                    <Box
                                                                        component="span"
                                                                        sx={{ color: '#9CA3AF', mx: 0.5 }}
                                                                    >
                                                                        —
                                                                    </Box>
                                                                    {item.renkKod}
                                                                </Box>
                                                            </TableCell>
                                                            <TableCell
                                                                    className={PS.cellProductName}
                                                                component="th"
                                                                scope="row"
                                                                sx={{ ...bodyCellSx, fontWeight: 500 }}
                                                            >
                                                                {item.productName}
                                                            </TableCell>
                                                                <TableCell className={PS.cellGender} component="th" scope="row" sx={bodyCellSx}>
                                                                {getGenderChip(item.gender)}
                                                            </TableCell>
                                                                <TableCell className={PS.cellCategory} component="th" scope="row" sx={bodyCellSx}>
                                                                {item.categories}
                                                            </TableCell>
                                                                <TableCell className={PS.cellStock} component="th" scope="row" sx={bodyCellSx}>
                                                                <Box
                                                                    sx={{
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        gap: 0.75,
                                                                    }}
                                                                >
                                                                    {isStock ? (
                                                                        <CheckCircleIcon
                                                                                className={PS.stockIconSuccess}
                                                                            sx={{
                                                                                color: '#16A34A',
                                                                                fontSize: '1.6rem',
                                                                            }}
                                                                        />
                                                                    ) : (
                                                                        <CancelIcon
                                                                                className={PS.stockIconError}
                                                                            sx={{
                                                                                color: '#DC2626',
                                                                                fontSize: '1.6rem',
                                                                            }}
                                                                        />
                                                                    )}
                                                                    <Box
                                                                            className={PS.stockText}
                                                                        component="span"
                                                                        sx={{
                                                                            fontSize: '1.5rem',
                                                                            fontWeight: 500,
                                                                            color: isStock ? '#15803D' : '#B91C1C',
                                                                        }}
                                                                    >
                                                                        {isStock
                                                                            ? t('productSearch.detail.inStock')
                                                                            : t('lostCargo.statusEnums.outOfStock')}
                                                                    </Box>
                                                                </Box>
                                                            </TableCell>
                                                                <TableCell className={PS.cellActions} component="th" scope="row" sx={bodyCellSx}>
                                                                <Link
                                                                        className={PS.detailLink}
                                                                    to={`/productDetail/${item.optionId}`}
                                                                    target="_blank"
                                                                    style={{ textDecoration: 'none' }}
                                                                >
                                                                    <Box
                                                                            className={PS.detailButton}
                                                                        sx={{
                                                                            display: 'inline-flex',
                                                                            alignItems: 'center',
                                                                            gap: 1,
                                                                            px: 2.5,
                                                                            py: 1,
                                                                            borderRadius: '8px',
                                                                            border: '1px solid #3B82F6',
                                                                            color: '#2563EB',
                                                                            fontWeight: 600,
                                                                            fontSize: '1.5rem',
                                                                            cursor: 'pointer',
                                                                            transition: 'all 0.15s ease',
                                                                            whiteSpace: 'nowrap',
                                                                            '&:hover': {
                                                                                backgroundColor: '#EFF6FF',
                                                                                borderColor: '#2563EB',
                                                                                boxShadow:
                                                                                    '0 2px 8px rgba(37,99,235,0.2)',
                                                                            },
                                                                        }}
                                                                    >
                                                                        {t('general.showDetail')}
                                                                        <VisibilityIcon sx={{ fontSize: '1.5rem' }} />
                                                                    </Box>
                                                                </Link>
                                                            </TableCell>
                                                        </TableRow>
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </Box>
                                    </Paper>
                                );
                                })}
                            </div>
                        ) : (
                            <div
                                className={PS.emptyState}
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignContent: 'center',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                }}
                            >
                                <div className={PS.emptyNoRecord}>
                                    <SCUINoRecordComponent />
                                </div>
                                <div className={PS.searchControls} style={{ marginTop: '2rem' }}>
                                    <SCUITextFieldWithSelectBox
                                        className={PS.searchPaper}
                                        placeholder={t('productSearch.search.placeholder')}
                                        buttonText={t('general.search')}
                                        value="1"
                                        selectList={selectListOptions}
                                        selectClassName={PS.searchSelect}
                                        inputClassName={PS.searchInput}
                                        onChangeSelect={(event: any) => {
                                            setDocumentType(event.target.value);
                                        }}
                                        selectValue={documentType}
                                        onChangeTextField={(event: any) => {
                                            setSearchedText(event.target.value);
                                        }}
                                        onClickButton={() => {
                                            listele();
                                        }}
                                        onKeyPressTextField={(e: any) => {
                                            if (e.key === 'Enter') {
                                                listele();
                                            }
                                        }}
                                    />
                                </div>
                                <FormControl className={PS.batchSection} style={{ width: 500, marginRight: '10px', marginTop: '3rem' }}>
                                    <SCUIButton
                                        className={PS.batchButton}
                                        text={t('general.batchSearch')}
                                        type="secondary"
                                        sizeType="fixed"
                                        size={'l'}
                                        onClick={() => {
                                            handleOpenModal();
                                        }}
                                    />
                                    <SCUIProductBatchSearchModal
                                        open={listModalOpen}
                                        onClose={handleCloseModal}
                                        orderNo={searchedText}
                                        setOrderNo={setSearchedText}
                                        listele={listele}
                                        text={selectListOptions[documentType - 1].text}
                                    />
                                </FormControl>
                            </div>
                        )}
                    </Grid>
                </Grid>
            </Grid>
        </SCUIContent>
    );
};
